import axios, { AxiosResponse } from 'axios';
import { Cookies } from '../../hooks/utility/useAppCookies';
import CookieStore from 'js-cookie';
import * as psl from 'psl';
import { ParsedDomain } from 'psl';
import moment from 'moment-timezone';

export type Response<T> = Promise<AxiosResponse<T>>;
export type RenderableResponse = AxiosResponse<{
  title: string;
  description: string;
  error_code: string;
}>;
export type ValidationErrorResponse = AxiosResponse<{ message: string; errors: string[] }>;
export type PaginatedResponse<T> = Response<PaginatedResponseData<T>>;

export type PaginatedResponseData<T> = {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
  };
};

export enum ResponseStatus {
  RenderableError = 412,
  RenderableErrorTwo = 417,
  ValidationError = 422,
}

const token = CookieStore.get(Cookies.AuthToken);

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;

axios.defaults.headers['Authorization'] = token ? `Bearer ${token}` : undefined;
axios.defaults.headers['Content-Type'] = 'application/json';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const { status } = response || {};

    if (status !== 401 || config.url === '/login' || config.url === '/refresh-token') {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshSubscribers.push((token) => {
          config.headers.Authorization = `Bearer ${token}`;

          resolve(axios(config));
        });
      });
    }

    isRefreshing = true;

    try {
      const { data } = await axios.post('/refresh-token');
      const newToken = data.access_token;

      // Notify all queued requests
      refreshSubscribers.forEach((callback) => callback(newToken));
      refreshSubscribers = [];

      // Retry the original request with new token
      config.headers.Authorization = `Bearer ${newToken}`;
      axios.defaults.headers['Authorization'] = `Bearer ${newToken}`;
      CookieStore.set(Cookies.AuthToken, newToken, {
        domain: (psl.parse(window.location.hostname) as ParsedDomain).domain ?? 'localhost',
        path: '/',
        expires: moment().add(399, 'days').toDate(),
      });

      return axios(config);
    } catch (refreshError) {
      CookieStore.remove(Cookies.AuthToken, {
        domain: (psl.parse(window.location.hostname) as ParsedDomain).domain ?? 'localhost',
        path: '/',
      });

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axios;
